import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import N8nWorkflowFlowModal from '../components/journeys/N8nWorkflowFlowModal'
import RequestChangeModal from '../components/journeys/RequestChangeModal'
import { listAdminN8nTemplates, listTenantN8nWorkflows } from '../api/adminN8nWorkflows'
import useManagerStore from '../store/managerStore'

const INDUSTRY_COLORS = {
  restaurant: 'bg-orange-100 text-orange-700 border-orange-200',
  spa: 'bg-purple-100 text-purple-700 border-purple-200',
  training: 'bg-blue-100 text-blue-700 border-blue-200',
  bakery: 'bg-pink-100 text-pink-700 border-pink-200',
  wholesale: 'bg-teal-100 text-teal-700 border-teal-200',
  generic: 'bg-gray-100 text-gray-600 border-gray-200',
}

function isCanceled(error) {
  return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError'
}

function getWorkflowId(workflow) {
  const id = workflow?.id ?? workflow?.workflow_id
  if (id === null || id === undefined || id === '') return null
  return String(id)
}

function workflowLabel(workflow) {
  if (workflow?.industry) return String(workflow.industry)
  if (!Array.isArray(workflow?.tags) || workflow.tags.length === 0) return ''
  const firstTag = workflow.tags[0]
  return typeof firstTag === 'string' ? firstTag : (firstTag?.name || '')
}

function sortWorkflowsActiveFirst(workflows) {
  return [...workflows].sort(
    (left, right) => Number(right?.is_activated === true) - Number(left?.is_activated === true)
  )
}

function WorkflowCard({ workflow, onViewFlow, onRequestChange, t }) {
  const workflowId = getWorkflowId(workflow)
  // `n8n_active` is the global runtime state.  The list is tenant-scoped, so
  // its status must reflect this tenant's assignment (`is_activated`).
  const isActive = workflow?.is_activated === true
  const label = workflowLabel(workflow)
  const colorClass = INDUSTRY_COLORS[label.toLowerCase()] || INDUSTRY_COLORS.generic
  const triggerCount = Number.isFinite(workflow?.trigger_count) ? workflow.trigger_count : '—'
  const nodeCount = Number.isFinite(workflow?.node_count)
    ? workflow.node_count
    : (Number.isFinite(workflow?.steps_count) ? workflow.steps_count : '—')
  const triggerText = t('automations.admin.workflows.triggerCount', { count: triggerCount })
  const nodeText = t('automations.admin.workflows.nodeCount', { count: nodeCount })
  const actionWorkflow = { ...workflow, id: workflowId }

  return (
    <article
      className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col gap-3 hover:shadow-md transition-shadow"
      data-workflow-id={workflowId || undefined}
    >
      {/* <div className="flex items-center justify-between min-h-6">
        {label ? (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${colorClass}`}>
            {label}
          </span>
        ) : <span />}
        {workflow?.version !== null && workflow?.version !== undefined && workflow?.version !== '' && (
          <span className="text-xs text-slate-400">v{workflow.version}</span>
        )}
      </div> */}

      <div className="font-semibold text-slate-800 text-sm leading-snug">
        {workflow?.name || t('automations.admin.workflows.unnamed')}
      </div>

      {workflow?.description && (
        <div className="text-xs text-slate-500 leading-relaxed line-clamp-2">
          {workflow.description}
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-slate-400">
        <span>⚡ {triggerText}</span>
        <span>·</span>
        <span>{nodeText}</span>
      </div>

      <div className="flex justify-start">
        {isActive ? (
          <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block shrink-0 mt-[1px]" />
            {t('automations.active')}
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block shrink-0 mt-[3px]" />
            {t('automations.inactive')}
          </span>
        )}
      </div>

      <div className="flex gap-2 mt-auto pt-2 border-t border-slate-100">
        {isActive ? (
          <>
            <button
              type="button"
              disabled={!workflowId}
              onClick={() => onViewFlow(actionWorkflow)}
              className="flex-1 text-xs font-medium py-1.5 px-3 bg-[#e8ecff] text-[#1535c8] rounded-md hover:bg-[#d4daff] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('automations.viewFlow')}
            </button>
            <button
              type="button"
              disabled={!workflowId}
              onClick={() => onRequestChange(actionWorkflow)}
              className="flex-1 text-xs font-medium py-1.5 px-3 border border-slate-200 text-slate-600 rounded-md hover:bg-slate-50 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('automations.requestChange')}
            </button>
          </>
        ) : (
          <>
            <span className="text-xs text-slate-400 flex items-center gap-1">
              🔒 {t('automations.admin.workflows.notActive')}
            </span>
            <button
              type="button"
              disabled={!workflowId}
              onClick={() => onRequestChange(actionWorkflow)}
              className="ml-auto text-xs font-medium px-3 py-1.5 border border-[#1535c8] text-[#1535c8] rounded-md hover:bg-[#e8ecff] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('automations.admin.workflows.contactToActivate')}
            </button>
          </>
        )}
      </div>
    </article>
  )
}

function WorkflowTemplateCard({ workflow, onViewFlow, onRequestChange, t }) {
  const workflowId = getWorkflowId(workflow)
  const actionWorkflow = { ...workflow, id: workflowId }
  const triggerCount = Number.isFinite(workflow?.trigger_count) ? workflow.trigger_count : '—'
  const nodeCount = Number.isFinite(workflow?.node_count)
    ? workflow.node_count
    : (Number.isFinite(workflow?.steps_count) ? workflow.steps_count : '—')

  return (
    <article
      className="bg-white rounded-xl border border-indigo-100 p-5 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow"
      data-workflow-template-id={workflowId || undefined}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600 bg-indigo-50 rounded-full px-2 py-1">
          {t('automations.admin.templates.badge')}
        </span>
        <span className="text-xs text-slate-400">{t('automations.admin.templates.ready')}</span>
      </div>
      <div className="font-semibold text-slate-800 text-sm leading-snug">
        {workflow?.name || t('automations.admin.workflows.unnamed')}
      </div>
      {workflow?.description && (
        <div className="text-xs text-slate-500 leading-relaxed line-clamp-2">{workflow.description}</div>
      )}
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <span>⚡ {t('automations.admin.workflows.triggerCount', { count: triggerCount })}</span>
        <span>·</span>
        <span>{t('automations.admin.workflows.nodeCount', { count: nodeCount })}</span>
      </div>
      <div className="flex gap-2 mt-auto pt-2 border-t border-slate-100">
        <button
          type="button"
          disabled={!workflowId}
          onClick={() => onViewFlow(actionWorkflow)}
          className="flex-1 text-xs font-medium py-1.5 px-3 bg-[#e8ecff] text-[#1535c8] rounded-md hover:bg-[#d4daff] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('automations.viewFlow')}
        </button>
        <button
          type="button"
          disabled={!workflowId}
          onClick={() => onRequestChange(actionWorkflow)}
          className="flex-1 text-xs font-medium py-1.5 px-3 border border-[#1535c8] text-[#1535c8] rounded-md hover:bg-[#e8ecff] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('automations.admin.templates.buy')}
        </button>
      </div>
    </article>
  )
}

export default function AutomationsPage() {
  const { t } = useTranslation()
  const tenantId = useManagerStore((state) => state.tenantId)
  const [workflows, setWorkflows] = useState([])
  const [status, setStatus] = useState('idle')
  const [retryKey, setRetryKey] = useState(0)
  const [flowWorkflow, setFlowWorkflow] = useState(null)
  const [changeWorkflow, setChangeWorkflow] = useState(null)
  const [resolvedTenantId, setResolvedTenantId] = useState(null)
  const [templates, setTemplates] = useState([])
  const [templateStatus, setTemplateStatus] = useState('idle')
  const requestSequenceRef = useRef(0)

  useEffect(() => {
    const controller = new AbortController()
    setTemplateStatus('loading')
    listAdminN8nTemplates({ signal: controller.signal })
      .then((data) => {
        setTemplates(Array.isArray(data?.workflows) ? data.workflows : [])
        setTemplateStatus('success')
      })
      .catch((error) => {
        if (isCanceled(error)) return
        setTemplates([])
        setTemplateStatus('error')
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (!tenantId) return undefined

    const controller = new AbortController()
    const requestSequence = ++requestSequenceRef.current

    listTenantN8nWorkflows(tenantId, { signal: controller.signal })
      .then((data) => {
        if (requestSequence !== requestSequenceRef.current) return
        const items = Array.isArray(data?.workflows) ? data.workflows : []
        setWorkflows(sortWorkflowsActiveFirst(items))
        setResolvedTenantId(tenantId)
        setStatus('success')
      })
      .catch((error) => {
        if (isCanceled(error) || requestSequence !== requestSequenceRef.current) return
        setWorkflows([])
        setResolvedTenantId(tenantId)
        setStatus('error')
      })

    return () => controller.abort()
  }, [retryKey, tenantId])

  const workflowStatus = tenantId && resolvedTenantId !== tenantId ? 'loading' : status

  return (
    <ResponsiveShell>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-900">
            {t('nav.automations')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('automations.subtitle')}
          </p>
        </div>

        <>
            {workflowStatus === 'loading' && (
              <div className="text-center py-16 text-slate-400 text-sm">
                {t('automations.loading')}
              </div>
            )}

            {workflowStatus === 'error' && (
              <div className="text-center py-16 text-sm">
                <p className="text-red-500">{t('automations.admin.errors.genericMessage')}</p>
                <button
                  type="button"
                  onClick={() => {
                    setStatus('loading')
                    setRetryKey((value) => value + 1)
                  }}
                  className="mt-4 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  {t('automations.admin.retry')}
                </button>
              </div>
            )}

            {workflowStatus === 'success' && workflows.length > 0 && (
              <div>
                <h2 className="text-base font-semibold text-slate-800 mb-1">{t('automations.admin.ownWorkflows.title')}</h2>
                <p className="text-xs text-slate-500 mb-4">{t('automations.admin.ownWorkflows.description')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {workflows.map((workflow, index) => (
                    <WorkflowCard key={getWorkflowId(workflow) ?? `missing-id-${index}`} workflow={workflow} onViewFlow={setFlowWorkflow} onRequestChange={setChangeWorkflow} t={t} />
                  ))}
                </div>
              </div>
            )}

            {workflowStatus === 'success' && workflows.length === 0 && (
              <div className="mb-8">
                <h2 className="text-base font-semibold text-slate-800 mb-1">{t('automations.admin.ownWorkflows.title')}</h2>
                <p className="text-sm text-slate-400">{t('automations.noTemplates')}</p>
              </div>
            )}

            <section className="mt-8 pt-8 border-t border-slate-200">
              <div className="mb-4">
                <h2 className="text-base font-semibold text-slate-800">{t('automations.admin.templates.title')}</h2>
                <p className="text-xs text-slate-500 mt-1">{t('automations.admin.templates.description')}</p>
              </div>
              {templateStatus === 'loading' && <div className="text-sm text-slate-400 py-6">{t('automations.admin.templates.loading')}</div>}
              {templateStatus === 'error' && <div className="text-sm text-red-500 py-6">{t('automations.admin.templates.error')}</div>}
              {templateStatus === 'success' && templates.length === 0 && <div className="text-sm text-slate-400 py-6">{t('automations.admin.templates.empty')}</div>}
              {templateStatus === 'success' && templates.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map((template, index) => (
                    <WorkflowTemplateCard key={getWorkflowId(template) ?? `missing-template-${index}`} workflow={template} onViewFlow={setFlowWorkflow} onRequestChange={setChangeWorkflow} t={t} />
                  ))}
                </div>
              )}
            </section>
        </>
      </div>

      <N8nWorkflowFlowModal
        open={Boolean(flowWorkflow)}
        onClose={() => setFlowWorkflow(null)}
        workflow={flowWorkflow}
      />

      <RequestChangeModal
        open={Boolean(changeWorkflow)}
        onClose={() => setChangeWorkflow(null)}
        tenantId={tenantId}
        template={changeWorkflow}
      />
    </ResponsiveShell>
  )
}
